// src/api/apiService.js
import axios from "./AxiosInstance";
import { toast } from "react-toastify";

const handleError = (error, showToast = true) => {
  let message;

  if (error.response) {
    console.log(error.response);
    message =
      error.response.data ||
      error.response.message ||
      "An error occurred while processing your request.";
  } else if (error.request) {
    message =
      "Network error: Please check your internet connection or try again later.";
  } else {
    message = error.message || "Something went wrong.";
  }

  if (showToast) toast.error(message);
  throw error;
};

// 🔄 No need for withCredentials anymore
const createOptions = (customOptions = {}) => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
    ...(customOptions.headers || {}),
  },
  ...customOptions,
});

const apiService = {
  getWithParams: async (resource, params, showToast = true) => {
    try {
      const res = await axios.get(`/${resource}`, createOptions({ params }));
      return res.data;
    } catch (error) {
      handleError(error, showToast);
    }
  },

  getAll: async (resource, options = {}, showToast = true) => {
    try {
      const res = await axios.get(`/${resource}`, createOptions(options));
      return res.data;
    } catch (error) {
      handleError(error, showToast);
    }
  },

  get: async (resource, pageSize, pageNumber, showToast = true) => {
    try {
      const res = await axios.get(
        `/${resource}?pageSize=${pageSize}&pageNumber=${pageNumber}`,
        createOptions()
      );
      return res.data;
    } catch (error) {
      handleError(error, showToast);
    }
  },

  getById: async (resource, id, showToast = true) => {
    try {
      const res = await axios.get(`/${resource}/${id}`, createOptions());
      return res.data;
    } catch (error) {
      handleError(error, showToast);
    }
  },

  getSingle: async (resource, showToast = true) => {
    try {
      const res = await axios.get(`/${resource}`, createOptions());
      return res.data;
    } catch (error) {
      handleError(error, showToast);
    }
  },

  create: async (resource, data, showToast = true) => {
    try {
      const res = await axios.post(`/${resource}`, data, createOptions());
      if (showToast) toast.success("Created successfully");
      return res.data;
    } catch (error) {
      handleError(error, showToast);
    }
  },
  flightbook: async (resource, data, showToast = true) => {
    try {
      const res = await axios.post(`/${resource}`, data, createOptions());
      if (res.data && res.data.status) {
        if (res.data.bookingStatus === "HOLD") {
          if (showToast) toast.success("Booking blocked successfully. Please complete the payment to confirm.");
        }
        else {
          if (showToast) toast.success("Booked successfully");
        }
        return res.data;
      } else {
        if (showToast) toast.error(res.data.message || "Booking failed");
        return res.data;
      }
    } catch (error) {
      handleError(error, showToast);
    }
  },

  post: async (resource, data = {}, showToast = true) => {
    try {
      const res = await axios.post(`/${resource}`, data, createOptions());
      return res.data;
    } catch (error) {
      handleError(error, showToast);
      return null;
    }
  },
  postWithFile: async (resource, formData, showToast = true) => {
    try {
      // We still use createOptions to get the Authorization header.
      // We DO NOT set the 'Content-Type' header manually.
      // The browser will automatically set it to 'multipart/form-data'
      // and add the necessary 'boundary' when it sees you're sending FormData.
      const res = await axios.post(`/${resource}`, formData, createOptions());

      if (showToast) toast.success("Sent successfully!");
      return res.data;
    } catch (error) {
      handleError(error, showToast);
    }
  },

  update: async (resource, data, showToast = true) => {
    try {
      const res = await axios.put(`/${resource}`, data, createOptions());
      if (showToast) toast.success("Updated successfully");
      return res.data;
    } catch (error) {
      handleError(error, showToast);
    }
  },

  put: async (resource, data = {}, showToast = true) => {
    try {
      const res = await axios.put(`/${resource}`, data, createOptions());
      return res.data;
    } catch (error) {
      handleError(error, showToast);
    }
  },
  remove: async (resource, id, showToast = true) => {
    try {
      const res = await axios.delete(`/${resource}/${id}`, createOptions());
      if (showToast) toast.success("Deleted successfully");
      return res.data;
    } catch (error) {
      handleError(error, showToast);
    }
  },
};

export default apiService;
